import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import type { InspoImportSaveSummary } from '@/components/InspoImport/inspoImportTypes';
import { TripImportFlow } from '@/components/TripImport/TripImportFlow';
import type { InspoImportEntrySource } from '@/contexts/InspoImportContext';
import { WebAnalytics } from '@/lib/analytics';
import { EVENT_INSPO_IMPORT_DEEP_LINK_OPENED, EVENT_INSPO_IMPORT_FLOW_OPENED } from '@/lib/analyticsEvents';
import { resolveInspoImportEntryChannel } from '@/utils/inspoImportEntryChannel';
import { getBackFallbackPath, getFromStatePath } from '@/utils/backNavigation';

import type { InspoImportFlowVariant } from './inspoImportTypes';

export type { InspoImportFlowVariant } from './inspoImportTypes';

interface InspoImportFlowProps {
  /** @deprecated Prefer `variant`. Kept for modal host compatibility. */
  isModal?: boolean;
  variant?: InspoImportFlowVariant;
  prefilledUrl?: string;
  entrySource?: InspoImportEntrySource;
  clearPrefilledUrl?: () => void;
  onRequestClose?: (redirectTo?: string) => void;
  onCompleteSave?: (summary: InspoImportSaveSummary) => void;
  onCancel?: () => void;
  autoStartImport?: boolean;
}

export function InspoImportFlow({
  isModal = false,
  variant,
  prefilledUrl = '',
  entrySource = 'in_app',
  clearPrefilledUrl,
  onRequestClose,
  onCompleteSave,
  onCancel,
  autoStartImport = false,
}: InspoImportFlowProps) {
  const resolvedVariant: InspoImportFlowVariant = variant ?? (isModal ? 'modal' : 'page');
  const isModalVariant = resolvedVariant === 'modal';
  const isOnboardingEmbed = resolvedVariant === 'onboarding';

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const sharedUrl = useMemo(
    () => (isModalVariant || isOnboardingEmbed ? prefilledUrl : (searchParams.get('url') ?? '')),
    [isModalVariant, isOnboardingEmbed, prefilledUrl, searchParams],
  );

  const tripEntry = entrySource === 'deep_link' ? 'inspo_import' : 'create_flyout';
  const shouldAutoStart = autoStartImport || entrySource === 'deep_link';

  useEffect(() => {
    WebAnalytics.track(EVENT_INSPO_IMPORT_FLOW_OPENED, {
      entry:
        entrySource === 'deep_link'
          ? 'deep_link'
          : isOnboardingEmbed
            ? 'onboarding'
            : isModalVariant
              ? 'modal'
              : 'page',
      has_url: Boolean(sharedUrl),
      entry_channel: resolveInspoImportEntryChannel(entrySource, false),
    });
    if (sharedUrl && entrySource === 'deep_link' && !isOnboardingEmbed) {
      WebAnalytics.track(EVENT_INSPO_IMPORT_DEEP_LINK_OPENED, {
        has_url: true,
        entry_channel: resolveInspoImportEntryChannel(entrySource, false),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateBackFromPage = useCallback(() => {
    const currentPathWithQuery = `${location.pathname}${location.search}`;
    const fromPath = getFromStatePath(location.state, currentPathWithQuery);
    if (fromPath) {
      navigate(fromPath);
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(getBackFallbackPath(location.pathname));
  }, [location.pathname, location.search, location.state, navigate]);

  const handleRequestClose = useCallback(() => {
    clearPrefilledUrl?.();
    onRequestClose?.();
    if (!isModalVariant && !isOnboardingEmbed) {
      navigateBackFromPage();
    }
  }, [clearPrefilledUrl, isModalVariant, isOnboardingEmbed, navigateBackFromPage, onRequestClose]);

  const handleCancel = useCallback(() => {
    clearPrefilledUrl?.();
    if (isOnboardingEmbed) {
      onCancel?.();
      return;
    }
    if (isModalVariant && onRequestClose) {
      onRequestClose();
      return;
    }
    navigateBackFromPage();
  }, [
    clearPrefilledUrl,
    isModalVariant,
    isOnboardingEmbed,
    navigateBackFromPage,
    onCancel,
    onRequestClose,
  ]);

  const handleSocialUrlAccepted = useCallback(
    (url: string) => {
      if (!isModalVariant && !isOnboardingEmbed) {
        const next = new URLSearchParams(searchParams);
        next.set('url', url);
        setSearchParams(next, { replace: true, state: location.state });
      }
    },
    [isModalVariant, isOnboardingEmbed, location.state, searchParams, setSearchParams],
  );

  const flow = (
    <TripImportFlow
      isModal={isModalVariant}
      variant={isOnboardingEmbed ? 'onboarding' : 'modal'}
      entry={tripEntry}
      prefilledSocialUrl={sharedUrl}
      socialOnly
      autoStartImport={shouldAutoStart}
      entrySource={entrySource}
      onRequestClose={handleRequestClose}
      onCompleteSave={onCompleteSave}
      onCancel={handleCancel}
      onSocialUrlAccepted={handleSocialUrlAccepted}
    />
  );

  if (isOnboardingEmbed || isModalVariant) {
    return flow;
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-[780px] flex-col justify-center bg-[#f5f5f5] px-4 py-6">
      {flow}
    </main>
  );
}
